import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { lastValueFrom, map, Subject, takeUntil } from 'rxjs';

import { AuthService } from '../../services/auth/auth.service';
import { AvisoService } from '../../services/aviso/aviso.service';
import { NotificationService } from '../../services/notification/notification.service';
import { PaginaInfo, paginasBreadcrumb, popPaginaAtiva } from '../../shared/activePage';
import { NotificacoesDetalhes } from '../../view/notificacoes/notificacoes-detalhes/notificacoes-detalhes';

@Component({
  selector: 'app-header',
  imports: [ReactiveFormsModule, RouterModule, CommonModule,],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit {
  userName = '';
  unreadAvisosCount = 0;
  unreadNotificacoesCount = 0;
  notificacoes: any[] = [];
  private destroy$ = new Subject<void>();

  // estados dos menus
  showUserMenu = signal(false);
  showNotificacoesMenu = signal(false);

  paginaInfo: PaginaInfo = { nome: '', rota: '' };
  paginas: PaginaInfo[] = [];

  private injector = inject(EnvironmentInjector);

  private paginaInfo$ = toObservable(paginasBreadcrumb, { injector: this.injector })
    .pipe(map((lista) => lista[lista.length - 1]));

  private paginas$ = toObservable(paginasBreadcrumb, { injector: this.injector });

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly el: ElementRef,
    private readonly avisoService: AvisoService,
    private readonly notificationService: NotificationService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.userName = this.authService.getClaim('nome');
  }

  ngOnInit(): void {
    this.paginaInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((info) => {
        this.paginaInfo = info;
        this.cdr.detectChanges();
      });

    this.paginas$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lista) => {
        this.paginas = lista;
        this.cdr.detectChanges();
      });

    this.notificationService.unreadNotificacoesCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.unreadNotificacoesCount = count;
      });

    this.notificationService.unreadAvisosCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.unreadAvisosCount = count;
      });

    this.loadUnreadCount();
  }

  navegarParaPagina(item: PaginaInfo | undefined, index: number): void {
    if (item?.rota) {
      this.router.navigate([item.rota], { queryParamsHandling: 'preserve' });
      popPaginaAtiva(index);
    }
  }

  private loadUnreadCount(): void {
    this.avisoService.getUnreadAvisosCount().subscribe({
      next: (response) => {
        this.notificationService.setUnreadAvisosCount(response.count);
      },
      error: (error) => {
        console.error('Erro ao carregar contagem de avisos não visualizados:', error);
      }
    });

    this.notificationService.getUnreadNotificacoesCountAPI().subscribe({
      next: (response) => {
        this.notificationService.setUnreadNotificacoesCount(response.count);
      },
      error: (error) => {
        console.error('Erro ao carregar contagem de notificações não visualizadas:', error);
      }
    })
  }

  logout() {
    sessionStorage.clear();
    window.location.href = '/auth/login';
  }

  goToPage(path: string) {
    this.router.navigateByUrl(path);
  }

  goToMeuPerfil() {
    const idUsuario = this.authService.getUsuarioId();
    this.router.navigate(['/usuarios', idUsuario]);
  }

  async toggleMenu(menu: 'user' | 'notif') {
    if (menu === 'user') {
      this.showUserMenu.set(!this.showUserMenu());
      this.showNotificacoesMenu.set(false);
    }

    if (menu === 'notif') {
      const willOpen = !this.showNotificacoesMenu();
      this.showNotificacoesMenu.set(willOpen);
      this.showUserMenu.set(false);

      if (willOpen) {
        await this.carregarNotificacoes();
      }
    }
  }

  private async carregarNotificacoes() {
    try {
      const notificacoesArray = await lastValueFrom(
        this.notificationService.getMinhasNotificacoes()
      );

      this.notificacoes = notificacoesArray.notificacoes.map((n) => ({
        ...n,
        status: n.status
          ? n.status.charAt(0).toUpperCase() + n.status.slice(1).toLowerCase()
          : '-',
        dataEnvio: this.formatarData(n.dataEnvio) || '-',
      }));

      this.notificacoes = this.notificacoes.slice(0, 6)
    } catch (err) {
      console.error('Falha ao buscar as notificações:', err);
    }
  }

  private formatarData(dataInput: Date | string): string {
    if (!dataInput) return '';
    const data = new Date(dataInput);
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  abrirPoliticaDePrivacidade() {
    const fileName = 'Política de Privacidade - Portal.pdf';
    const pdfUrl = 'assets/docs/' + encodeURIComponent(fileName);
    window.open(pdfUrl, '_blank');
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const clickedInside = this.el.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showUserMenu.set(false);
      this.showNotificacoesMenu.set(false);
    }
  }

  openDetailsModal(notificacaoClicada: any) {
    console.log(notificacaoClicada)
    const jaEstavaVisualizado =
      notificacaoClicada.status?.toLowerCase() === 'visualizado';

    const dialogRef = this.dialog.open(NotificacoesDetalhes, {
      width: '600px',
      data: {
        notificacaoClicada: notificacaoClicada,
        notificacoes: this.notificacoes
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.carregarNotificacoes();
    });

    if (!jaEstavaVisualizado) {
      notificacaoClicada.status = 'visualizado';
      this.notificacoes = [...this.notificacoes];

      this.notificationService
        .visualizarNotificacao(notificacaoClicada.id)
        .subscribe({
          next: () => {
            this.carregarNotificacoes();
            this.notificationService.handleNotificacaoVisualized();
          },
          error: (err) => {
            console.error('Erro ao marcar notificação como visualizada', err);
          },
        });
    }
  }
}
